import LogoBars from "@/components/brand/LogoBars";

interface Props {
  /** aggregate percent 0..100, or null when no active upload */
  percent: number | null;
  /** total files being uploaded (for label copy) */
  activeCount?: number;
  totalCount?: number;
  tone?: "light" | "dark";
  className?: string;
  status?: string;
}

/**
 * Branded aggregate upload progress: the codelumen four-bar mark
 * fills 0–25 % → bar 1, 25–50 % → bar 2, 50–75 % → bar 3, 75–100 % → bar 4.
 * Numeric percentage + plain-language status render alongside so the mark
 * is never the only signal.
 */
const UploadProgressMark = ({
  percent,
  activeCount,
  totalCount,
  tone = "dark",
  className = "",
  status,
}: Props) => {
  const pct = percent == null ? 100 : Math.max(0, Math.min(100, percent));
  const progress = pct / 25; // 0..4, fractional
  const done = percent == null;
  const label =
    status ??
    (done
      ? "All files uploaded"
      : activeCount && totalCount && totalCount > 1
        ? `Uploading ${activeCount} of ${totalCount} files… ${pct}%`
        : `Uploading… ${pct}%`);
  const textColor = tone === "dark" ? "text-white/70" : "text-charcoal/70";
  const eyebrowColor = tone === "dark" ? "text-white/40" : "text-charcoal/50";
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <LogoBars
        variant="progress"
        progress={progress}
        tone={tone === "dark" ? "light" : "dark"}
        size="sm"
        ariaLabel={`Upload progress ${pct}%`}
      />
      <div className="min-w-0">
        <p className={`font-sans font-light text-[11px] tracking-[0.15em] uppercase ${eyebrowColor}`}>
          {done ? "Ready" : "Uploading"}
        </p>
        <p className={`font-sans font-light text-xs ${textColor}`}>{label}</p>
      </div>
    </div>
  );
};

export default UploadProgressMark;
